import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, Navigate, Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft, FileText } from 'lucide-react';
import { useArticleList } from '../hooks/useArticleList';
import { topicBySlug } from '../data/helpTopics';
import { articlePath, topicTitle, topicUrl } from '../seo/helpMeta';
import '../App.css';

const HelpTopicPage: React.FC = () => {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const topic = topicBySlug(slug);
  const { list, loading } = useArticleList();

  if (!topic) return <Navigate to="/help" replace />;

  const arts = list
    .filter(a => a.topic === topic.title)
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <>
    <Helmet>
      <title>{topicTitle(topic)}</title>
      <meta name="description" content={topic.desc} />
      <link rel="canonical" href={topicUrl(topic.slug)} />
    </Helmet>
    <div className="help-page">

      <div className="help-crumbs">
        <a href="/">Chaptery</a><span className="sep">/</span>
        <a href="/help" onClick={e => { e.preventDefault(); navigate('/help'); }}>Help Center</a>
        <span className="sep">/</span><span>{topic.title}</span>
      </div>

      <section className="help-hero">
        <div className="help-hero-inner">
          <span className="eyebrow">Help Center</span>
          <h1>{topic.title}</h1>
          <p>{topic.desc}</p>
        </div>
      </section>

      <div className="help-wrap">
        <section className="help-section">
          <div className="help-section-head">
            <h2>{loading ? 'Articles' : `${arts.length} article${arts.length === 1 ? '' : 's'}`}</h2>
          </div>

          {!loading && arts.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary, #555)' }}>No articles in this topic yet.</p>
          ) : (
            <ul className="help-popular-list">
              {arts.map(a => (
                <li key={a.slug}>
                  <a
                    href={articlePath(a.slug)}
                    onClick={e => { e.preventDefault(); navigate(articlePath(a.slug)); }}
                  >
                    <span className="pop-icon"><FileText size={17} /></span>
                    {a.title}
                    {a.meta?.read && <span className="pop-tag">{a.meta.read}</span>}
                    <span className="pop-arrow"><ChevronRight size={16} /></span>
                  </a>
                </li>
              ))}
            </ul>
          )}

          <div style={{ marginTop: 24 }}>
            <Link
              className="btn-secondary"
              to="/help"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px' }}
            >
              <ArrowLeft size={15} /> Back to Help Center
            </Link>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default HelpTopicPage;
